import { useEffect, useState } from 'react';

import { envConfig } from '#constants/envConfig';

/** Поле, над которым телефон поднимает клавиатуру. Кнопки и флажки клавиатуры не зовут. */
const TEXT_INPUT_TYPES = new Set(['text', 'search', 'email', 'url', 'tel', 'password', 'number']);

export const isTypingField = (element: Element | null): boolean => {
  if (!(element instanceof HTMLElement)) {
    return false;
  }
  if (element.isContentEditable) {
    return true;
  }
  if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)) {
    return false;
  }
  // Поле только для чтения клавиатуру не зовёт: так на телефоне выглядят выпадашки без поиска
  // (AutocompleteSelectInput с `searchable={false}`), где фокус в поле есть, а печатать нечего.
  if (element.readOnly || element.disabled || element.inputMode === 'none') {
    return false;
  }
  return element instanceof HTMLTextAreaElement || TEXT_INPUT_TYPES.has(element.type);
};

/**
 * Насколько видимая область должна стать ниже полной, чтобы считать клавиатуру открытой.
 * Строка адреса в мобильном Chrome прячется и появляется на ~56px, клавиатура отнимает 250+.
 */
export const KEYBOARD_MIN_HEIGHT_PX = 150;

/**
 * Человек сейчас печатает – в фокусе поле ввода и клавиатура не убрана.
 *
 * Начинаем с фокуса, а не с высоты окна: в приложении клавиатура сжимает сам вебвью
 * (`Keyboard.resize: 'native'`), и `innerHeight` с `visualViewport` уменьшаются вместе –
 * сравнивать их не с чем. Фокус в поле одинаково честен в браузере и в приложении и приходит
 * раньше самой клавиатуры.
 *
 * Общий для полосы созвона (уезжает наверх) и нижней навигации (прячется): обоим мешает одно и то
 * же – клавиатура, а не сам фокус.
 */
export const useTypingFieldFocused = () => {
  // Начинаем с `false` и сверяемся с фокусом уже после гидратации: хук стоит в серверном каркасе,
  // и человек, тапнувший в поле до прихода скриптов, иначе получил бы разметку, расходящуюся с
  // серверной.
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    // `focusout` приходит раньше, чем фокус встаёт на следующий элемент: переход между двумя
    // полями мигнул бы полосой. Поэтому смотрим на `activeElement` в следующем кадре.
    let frame = 0;
    const sync = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setFocused(isTypingField(document.activeElement)));
    };

    setFocused(isTypingField(document.activeElement));
    document.addEventListener('focusin', sync);
    document.addEventListener('focusout', sync);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('focusin', sync);
      document.removeEventListener('focusout', sync);
    };
  }, []);

  /*
    «Назад» на Android убирает клавиатуру, а фокус оставляет в поле: по одному фокусу полоса и
    навигация так и не вернулись бы, пока человек не ткнёт мимо. Поэтому кроме фокуса слушаем саму
    клавиатуру, а фокус нужен лишь для первого кадра.

    В приложении клавиатура сообщает о себе сама. В браузере её выдаёт видимая область: открытая
    клавиатура делает её заметно ниже полной высоты. Полную высоту запоминаем, а не берём из
    `innerHeight`: во встроенных браузерах и с `interactive-widget=resizes-content` он сжимается
    вместе с ней. На компьютере видимая область не сжимается вовсе – там остаётся чистый фокус.
  */
  const [keyboardShown, setKeyboardShown] = useState(true);

  useEffect(() => {
    if (envConfig.IS_NATIVE || typeof window === 'undefined' || !window.visualViewport) {
      return undefined;
    }
    const viewport = window.visualViewport;
    // `null` – полная высота неизвестна: экран повернули при открытой клавиатуре.
    let fullHeight: number | null = viewport.height;
    let lastHeight = viewport.height;
    let width = viewport.width;
    let sawKeyboard = false;

    const onResize = () => {
      const height = viewport.height;
      // Поворот экрана меняет полную высоту – меряем заново. Если клавиатура при этом открыта,
      // текущая высота урезана ею, и полной станет первая, что окажется заметно больше.
      if (Math.abs(viewport.width - width) > 1) {
        width = viewport.width;
        fullHeight = sawKeyboard ? null : height;
        lastHeight = height;
        return;
      }
      if (fullHeight === null) {
        if (height - lastHeight <= KEYBOARD_MIN_HEIGHT_PX) {
          lastHeight = height;
          return;
        }
        fullHeight = height;
      }
      lastHeight = height;
      fullHeight = Math.max(fullHeight, height);
      if (fullHeight - height > KEYBOARD_MIN_HEIGHT_PX) {
        sawKeyboard = true;
        setKeyboardShown(true);
      } else if (sawKeyboard) {
        // Закрытой считаем только ту клавиатуру, которую видели открытой: пока она выезжает,
        // высота ещё полная, и без этого условия навигация мигнула бы на первом кадре.
        sawKeyboard = false;
        setKeyboardShown(false);
      }
    };

    // Новое поле – новая клавиатура: снова верим фокусу, пока она не откроется.
    const onFocusIn = () => setKeyboardShown(true);

    viewport.addEventListener('resize', onResize);
    document.addEventListener('focusin', onFocusIn);

    return () => {
      viewport.removeEventListener('resize', onResize);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, []);

  useEffect(() => {
    if (!envConfig.IS_NATIVE) {
      return undefined;
    }

    let disposed = false;
    const handles: { remove: () => Promise<void> }[] = [];

    const subscribe = async () => {
      const { Keyboard } = await import('@capacitor/keyboard');
      const shown = await Keyboard.addListener('keyboardWillShow', () => setKeyboardShown(true));
      const hidden = await Keyboard.addListener('keyboardWillHide', () => setKeyboardShown(false));
      handles.push(shown, hidden);
      if (disposed) {
        for (const handle of handles) {
          void handle.remove();
        }
      }
    };
    subscribe().catch(() => undefined);

    return () => {
      disposed = true;
      for (const handle of handles) {
        void handle.remove();
      }
    };
  }, []);

  return focused && keyboardShown;
};
